// ============================================
// LISTE DES DOSSIERS — puces de statut et légende
// ============================================
// Au-dessus de la liste : une puce par statut, dans sa couleur, avec son
// icône et son nombre de dossiers. Les puces pilotent le même filtre de
// statut que la liste déroulante (filters.status) : « À traiter » (défaut),
// « Tous », ou un statut précis ; recliquer la puce active revient à
// « À traiter ». Dessous, une légende repliable explique chaque statut.
//
// Archives (19/10/2026) : « Fermé » n'a plus de puce, les dossiers fermés sont
// dans l'onglet Archives. « Tous » = tous les dossiers non archivés ; un lien
// discret « Voir les archives (N) » termine la rangée.

import React from 'react';
import { Archive } from 'lucide-react';
import { TicketStatus } from '../../types';
import { IconeStatut, BadgeStatut, couleurStatut, libelleStatut } from '../../shared/ui/BadgeStatut';
import { SENS_STATUT } from '../../shared/statuts';
import { A_TRAITER } from './listeDossiers';

/** Ordre des puces : le parcours, les états particuliers à côté de leur étape (sans « Fermé » : archives). */
export const ORDRE_PUCES: TicketStatus[] = [
  TicketStatus.OPEN,
  TicketStatus.REOPENED,
  TicketStatus.IN_PROGRESS,
  TicketStatus.ESCALATED,
  TicketStatus.WAITING_CUSTOMER,
  TicketStatus.RESOLVED,
];

/** Légende : tous les statuts, « Fermé » (archivé) compris. */
export const ORDRE_LEGENDE: TicketStatus[] = [...ORDRE_PUCES, TicketStatus.CLOSED];

export type CompteursStatut = Partial<Record<TicketStatus | string, number>> | null | undefined;

/**
 * Nombres affichés : « À traiter » = tout sauf résolu et fermé ; « Tous » =
 * tous les non archivés (sans les fermés) ; `archives` = les fermés.
 */
export function nombresPuces(compteurs: CompteursStatut): { aTraiter: number; tous: number; archives: number; parStatut: Record<TicketStatus, number> } | null {
  if (!compteurs) return null;
  const parStatut = Object.fromEntries(ORDRE_LEGENDE.map(s => [s, Math.max(0, Number(compteurs[s]) || 0)])) as Record<TicketStatus, number>;
  const tous = ORDRE_PUCES.reduce((n, s) => n + parStatut[s], 0);
  return { aTraiter: tous - parStatut[TicketStatus.RESOLVED], tous, archives: parStatut[TicketStatus.CLOSED], parStatut };
}

/** Nouvelle valeur du filtre quand on clique une puce : la puce active se désactive (retour à « À traiter »). */
export const valeurApresClic = (actuelle: string, cliquee: string): string =>
  actuelle === cliquee && cliquee !== A_TRAITER ? A_TRAITER : cliquee;

const PUCE = 'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition-colors max-lg:min-h-11 max-lg:px-3.5 max-lg:text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sapin-600';

export const PucesStatut: React.FC<{
  valeur: string;
  onChange: (valeur: string) => void;
  compteurs?: CompteursStatut;
  /** Infobulle des nombres (portée des statistiques). */
  noteCompteurs?: string;
  /** Lien « Voir les archives » en fin de rangée (absent : pas de lien). */
  onVoirArchives?: () => void;
}> = ({ valeur, onChange, compteurs, noteCompteurs, onVoirArchives }) => {
  const nombres = nombresPuces(compteurs);
  const nombre = (n: number | undefined) => (n === undefined ? null : (
    <span className="tnum rounded-full bg-black/5 px-1.5 text-[11px]" title={noteCompteurs}>{n}</span>
  ));
  const neutre = (v: string, libelle: string, n: number | undefined) => {
    const actif = valeur === v;
    return (
      <button key={v || 'tous'} type="button" aria-pressed={actif} onClick={() => onChange(valeurApresClic(valeur, v))}
        className={`${PUCE} ${actif ? 'bg-encre border-encre text-white' : 'bg-white border-trait text-encre hover:bg-brume'}`}>
        {libelle}{nombre(n)}
      </button>
    );
  };
  return (
    // Sur téléphone, les puces défilent à l'horizontale.
    <div role="group" aria-label="Filtrer par statut" data-testid="puces-statut"
      className="flex gap-2 overflow-x-auto overscroll-x-contain pb-1 lg:flex-wrap">
      {neutre(A_TRAITER, 'À traiter', nombres?.aTraiter)}
      {neutre('', 'Tous', nombres?.tous)}
      {ORDRE_PUCES.map(s => {
        const actif = valeur === s;
        const couleur = couleurStatut(s);
        return (
          <button key={s} type="button" aria-pressed={actif} onClick={() => onChange(valeurApresClic(valeur, s))}
            title={`${libelleStatut(s)} : ${SENS_STATUT[s].resume}`}
            data-statut={s}
            className={`${PUCE} ${actif ? 'text-white' : 'bg-white text-encre hover:bg-brume'}`}
            style={actif ? { backgroundColor: couleur, borderColor: couleur } : { borderColor: `${couleur}55` }}>
            <IconeStatut status={s} className="w-3.5 h-3.5" style={actif ? undefined : { color: couleur }} />
            {libelleStatut(s)}
            {nombre(nombres?.parStatut[s])}
          </button>
        );
      })}
      {onVoirArchives && (
        <button type="button" onClick={onVoirArchives} data-testid="lien-archives"
          title="Les dossiers fermés : rien n’est supprimé, un e-mail du client rouvre son dossier"
          className="inline-flex items-center gap-1.5 px-2 py-1.5 text-xs font-semibold whitespace-nowrap text-graphite underline-offset-2 hover:text-encre hover:underline max-lg:min-h-11 max-lg:text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sapin-600">
          <Archive className="w-3.5 h-3.5" aria-hidden="true" />
          Voir les archives{nombres ? <span className="tnum">{` (${nombres.archives})`}</span> : null}
        </button>
      )}
    </div>
  );
};

/** Légende repliable : ce que veut dire chaque statut. */
export const LegendeStatuts: React.FC<{ className?: string }> = ({ className = '' }) => (
  <details className={`group text-sm ${className}`}>
    <summary className="inline-flex items-center gap-1 cursor-pointer select-none text-xs font-semibold text-sapin-700 hover:text-sapin-900 max-lg:min-h-11 max-lg:text-sm">
      Que veulent dire les statuts ?
    </summary>
    <dl className="mt-2 grid gap-x-6 gap-y-2 sm:grid-cols-2 surface p-3">
      {ORDRE_LEGENDE.map(s => (
        <div key={s} className="flex items-start gap-2">
          <dt className="flex-shrink-0 w-32"><BadgeStatut status={s} /></dt>
          <dd className="text-xs text-graphite leading-relaxed">
            <span className="text-encre">{SENS_STATUT[s].resume}</span> {SENS_STATUT[s].attente}
          </dd>
        </div>
      ))}
    </dl>
  </details>
);
